import React from 'react';
import {
  Box,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Button,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  SmartToy as AgentsIcon,
  MenuBook as KnowledgeIcon,
  Extension as IntegrationsIcon,
  VpnKey as ExternalApiIcon,
  Build as ToolsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UI, ROUTES } from '../../constants';

const navigationItems = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: ROUTES.DASHBOARD,
    permission: 'agents:view',
  },
  {
    text: 'Agentes',
    icon: <AgentsIcon />,
    path: ROUTES.AGENTS,
    permission: 'agents:view',
  },
  {
    text: 'Base de Conhecimento',
    icon: <KnowledgeIcon />,
    path: ROUTES.KNOWLEDGE_BASE,
    permission: 'knowledge:view',
  },
  {
    text: 'Integrações',
    icon: <IntegrationsIcon />,
    path: ROUTES.INTEGRATIONS,
    permission: 'integrations:view',
  },
  {
    text: 'API Keys',
    icon: <ExternalApiIcon />,
    path: ROUTES.EXTERNAL_API,
    permission: 'external_api:view',
  },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, company, logout, hasPermission } = useAuth();

  const handleNavigation = (path) => {
    // Verificar se há um evento personalizado para interceptar a navegação
    const event = new CustomEvent('checkUnsavedChanges', {
      detail: { path },
      cancelable: true
    });
    
    // Se o evento for cancelado (preventDefault), não navegue
    if (window.dispatchEvent(event)) {
      navigate(path);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      case 'editor':
        return 'info';
      case 'viewer':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'owner':
        return 'Proprietário';
      case 'admin':
        return 'Administrador';
      case 'editor':
        return 'Editor';
      case 'viewer':
        return 'Visualizador';
      default:
        return role;
    }
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: UI.SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: UI.SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Logo */}
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src="/assets/FÁBRICA.png"
              alt="FÁBRICA"
              style={{
                height: '56px',
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
              onError={(e) => {
                // Fallback para texto se a imagem não carregar
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                fontSize: '1.5rem',
                display: 'none', // Será mostrado apenas se a imagem falhar
              }}
            >
              FÁBRICA
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Navigation */}
        <Box sx={{ flexGrow: 1, p: 2 }}>
          <List sx={{ p: 0 }}>
            {navigationItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              const canAccess = hasPermission(item.permission) || user?.role === 'admin' || user?.role === 'owner';

              if (!canAccess) return null;

              return (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={motion.div}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      bgcolor: isActive ? 'primary.main' : 'transparent',
                      color: isActive ? 'primary.contrastText' : 'text.primary',
                      '&:hover': {
                        bgcolor: isActive ? 'primary.dark' : 'action.hover',
                      },
                      '& .MuiListItemIcon-root': {
                        color: isActive ? 'primary.contrastText' : 'text.secondary',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: isActive ? 600 : 500,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Divider />

        {/* User Info */}
        <Box sx={{ p: 2 }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              p: 1.5,
              bgcolor: 'background.default',
              borderRadius: 2,
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                mr: 1.5,
                bgcolor: 'primary.main',
                fontSize: '1rem',
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.name || 'Usuário'}
                </Typography>
                <Chip
                  size="small"
                  label={getRoleLabel(user?.role)}
                  color={getRoleColor(user?.role)}
                  sx={{ fontSize: '0.7rem', height: '16px' }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {company?.name || user?.company?.name || 'Empresa'}
              </Typography>
            </Box>
          </Box>

          {/* Logout Button */}
          <Button
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            fullWidth
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Sair
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar; 